<template>
  <div>
    <div class="row justify-between items-center q-pa-sm bg-grey-2 border-bottom sticky-top">
      <div class="text-caption text-grey-7 text-weight-bold">PITCHING REPORTS</div>
      <q-btn
        icon="print"
        label="Print Report"
        color="primary"
        unelevated
        size="sm"
        @click="printPitchingSummary"
        :disable="activePitchingStats.length === 0"
      />
    </div>
    <q-list separator>
      <q-item
        v-for="pitcher in activePitchingStats"
        :key="'pitcher-' + pitcher.id"
        class="q-py-md"
      >
        <q-item-section avatar>
          <q-avatar color="primary" text-color="white">
            {{ pitcher.uniformNumber }}
          </q-avatar>
        </q-item-section>

        <q-item-section>
          <q-item-label class="text-weight-bold text-subtitle1">{{
              pitcher.name
            }}</q-item-label>
          <q-item-label caption class="q-mt-xs">
            Total Pitches: <span class="text-weight-bold text-black">{{ pitcher.total }}</span>
          </q-item-label>

          <!-- Quick breakdown bar -->
          <div class="row q-gutter-x-sm q-mt-xs text-caption">
            <div class="text-positive text-weight-bold">Balls: {{ pitcher.balls }}</div>
            <div class="text-negative text-weight-bold">Strikes: {{ pitcher.strikes }}</div>
            <div class="text-grey-7 text-italic">
              ({{
                pitcher.total > 0 ? Math.round((pitcher.strikes / pitcher.total) * 100) : 0
              }}% Strikes,
              {{
                pitcher.total > 0
                  ? 'Whiff ' +
                  Math.round((pitcher.whiffs / pitcher.total) * 100).toString() +
                  '%'
                  : '0%'
              }})
            </div>
          </div>

          <!-- NEW: Battery & Running Game Context -->
          <div class="q-mt-sm text-body2">
            <div class="q-mb-xs">
              <span class="text-weight-bold text-grey-8">Catcher(s): </span>
              {{ pitcher.catchers.join(', ') || 'Unknown' }}
            </div>

            <!-- Pickoffs -->
            <div v-if="pitcher.pickoffs.length > 0" class="q-mb-xs">
              <span class="text-weight-bold text-grey-8">Pickoff Attempts:</span>
              <ul class="q-pl-md q-my-none text-caption text-grey-9">
                <li v-for="(po, i) in pitcher.pickoffs" :key="'po-'+i">{{ po }}</li>
              </ul>
            </div>

            <!-- Stolen Bases -->
            <div v-if="pitcher.stolenBases.length > 0" class="q-mb-xs">
              <span class="text-weight-bold text-grey-8">Stolen Bases Allowed:</span>
              <ul class="q-pl-md q-my-none text-caption text-grey-9">
                <li v-for="(sb, i) in pitcher.stolenBases" :key="'sb-'+i">{{ sb }}</li>
              </ul>
            </div>

            <!-- Passed Balls -->
            <div v-if="pitcher.passedBalls.length > 0">
              <span class="text-weight-bold text-grey-8">Passed Balls:</span>
              <ul class="q-pl-md q-my-none text-caption text-grey-9">
                <li v-for="(pb, i) in pitcher.passedBalls" :key="'pb-'+i">{{ pb }}</li>
              </ul>
            </div>
          </div>
        </q-item-section>

        <!-- DUAL MINI CHARTS -->
        <q-item-section side>
          <div class="row q-gutter-x-sm">
            <!-- PITCH GRID -->
            <div
              class="bg-white border-all rounded-borders q-pa-xs shadow-1 column items-center"
            >
              <div
                class="text-primary text-weight-bold q-mb-xs"
                style="font-size: 0.55rem; letter-spacing: 0.5px"
              >
                PITCHER POV
              </div>
              <svg
                viewBox="0 0 100 100"
                width="70"
                height="70"
                style="background-color: #f5f5f5; border-radius: 4px"
              >
                <rect
                  x="20"
                  y="25"
                  width="60"
                  height="50"
                  fill="white"
                  stroke="#333"
                  stroke-width="1.5"
                />
                <line x1="20" y1="41.6" x2="80" y2="41.6" stroke="#999" stroke-width="0.5" />
                <line x1="20" y1="58.3" x2="80" y2="58.3" stroke="#999" stroke-width="0.5" />
                <line x1="40" y1="25" x2="40" y2="75" stroke="#999" stroke-width="0.5" />
                <line x1="60" y1="25" x2="60" y2="75" stroke="#999" stroke-width="0.5" />
                <circle
                  v-for="(p, idx) in pitcher.pitches"
                  :key="'mini-pitch-' + idx"
                  :cx="20 + p.x * 60"
                  :cy="25 + p.y * 50"
                  r="3.5"
                  :fill="p.color"
                  stroke="#2c3e50"
                  stroke-width="0.5"
                  opacity="0.85"
                />
              </svg>
            </div>

            <!-- BATTED BALLS (SPRAY CHART) -->
            <div
              class="bg-white border-all rounded-borders q-pa-xs shadow-1 column items-center"
            >
              <div
                class="text-primary text-weight-bold q-mb-xs"
                style="font-size: 0.55rem; letter-spacing: 0.5px"
              >
                BATTED BALLS
              </div>
              <svg
                viewBox="0 0 100 100"
                width="70"
                height="70"
                style="background-color: #f5f5f5; border-radius: 4px"
              >
                <path
                  d="M 7.6 42.6 Q 50 15 92.4 42.6 L 50 85 Z"
                  fill="white"
                  stroke="#ccc"
                  stroke-width="1.5"
                />
                <path
                  d="M 30 65 Q 50 50 70 65 L 50 85 Z"
                  fill="#eee"
                  stroke="#ccc"
                  stroke-width="1"
                />
                <path
                  d="M 50 82 L 40 72 L 50 62 L 60 72 Z"
                  fill="none"
                  stroke="#ccc"
                  stroke-width="1"
                />
                <circle
                  v-for="(hit, idx) in pitcher.battedBalls"
                  :key="'mini-hit-' + idx"
                  :cx="50 + hit.x * 75"
                  :cy="85 + hit.y * 75"
                  r="3.5"
                  :fill="hit.color"
                  stroke="#2c3e50"
                  stroke-width="0.5"
                  opacity="0.85"
                />
              </svg>
            </div>
          </div>
        </q-item-section>
      </q-item>

      <div v-if="activePitchingStats.length === 0" class="q-pa-xl text-center text-grey-6">
        <q-icon name="speed" size="xl" color="grey-4" class="q-mb-sm" />
        <div>No pitching data recorded yet.</div>
      </div>
    </q-list>
  </div>
</template>
<script setup lang="ts">
import { computed } from 'vue'
import { useGameStore } from 'stores/game-store'
import type {
  PitchTransaction,
  PlayTransaction, RunnerAdvanceTransaction, SequenceItem,
} from 'src/stores/base-types'

const gameStore = useGameStore()

const props = defineProps<{
  teamLineup: 'home' | 'away' | 'all'
}>()

// --- PITCHING STATS LOGIC ---
const activePitchingStats = computed(() => {
  // Correctly handle the 'all' option to combine both rosters
  const lineup = props.teamLineup === 'all'
    ? [...(gameStore.homeLineup || []), ...(gameStore.visitingLineup || [])]
    : props.teamLineup === 'home' ? gameStore.homeLineup : gameStore.visitingLineup;

  if (!lineup) return []

  const allPlayers = lineup.concat(lineup.flatMap((l) => l.substitutions || []))
  const teamPlayerIds = allPlayers.map((p) => p.player.id)

  // Grab a master roster of everyone in the game so we can identify opposing base runners
  const allGamePlayers = [
    ...(gameStore.homeLineup || []),
    ...(gameStore.visitingLineup || [])
  ].flatMap((l) => [l, ...(l.substitutions || [])])

  const pitcherTxns = gameStore.transactions.filter(
    (t) => t.pitcherId != null && teamPlayerIds.includes(t.pitcherId)
  )

  const allLinkedPlays = gameStore.transactions.filter(
    (t) => t.eventType === 'PLAY'
  ) as PlayTransaction[]

  const statsMap = new Map()

  pitcherTxns.forEach((t) => {
    const pId = t.pitcherId!

    if (!statsMap.has(pId)) {
      const playerEntry = allPlayers.find((p) => p.player.id === pId)
      statsMap.set(pId, {
        id: pId,
        name: playerEntry?.player.name || `Unknown (${pId})`,
        uniformNumber: playerEntry?.player.uniformNumber || '-',
        total: 0,
        balls: 0,
        strikes: 0,
        whiffs: 0,
        pitches: [] as { x: number; y: number; color: string }[],
        battedBalls: [] as { x: number; y: number; color: string }[],
        catchers: [] as string[],
        pickoffs: [] as string[],
        stolenBases: [] as string[],
        passedBalls: [] as string[]
      })
    }

    const stat = statsMap.get(pId)

    // PROCESS PITCHES
    if (t.eventType === 'PITCH') {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
      const pitchTxn = t as PitchTransaction
      stat.total++
      const outcome = pitchTxn.pitchOutcome || 'B'

      let color = '#7f8c8d'
      if (['B', 'W', 'H', 'I', 'V', 'P'].includes(outcome)) {
        stat.balls++
        color = '#27ae60' // Ball
      } else {
        stat.strikes++
        if (['S', 'K', 'Q'].includes(outcome)) {
          stat.whiffs++
        }
        if (outcome === 'X') {
          color = '#2980b9' // In Play
        } else {
          color = '#e74c3c' // Strike
        }
      }

      stat.pitches.push({
        x: 1 - (pitchTxn.pitchX ?? 0.5),
        y: pitchTxn.pitchY ?? 0.5,
        color,
      })

      if (pitchTxn.hitLocationX != null && pitchTxn.hitLocationY != null) {
        const linkedPlay = allLinkedPlays.find((pl) => String(pl.linkedPitchId) === String(t.id))
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const resolvedOutcome = linkedPlay?.outcome || (pitchTxn as any).outcome || outcome

        const isHit = ['S', 'D', 'T', 'HR', '1B', '2B', '3B'].includes(resolvedOutcome)

        stat.battedBalls.push({
          x: pitchTxn.hitLocationX,
          y: pitchTxn.hitLocationY,
          color: isHit ? '#2980b9' : '#e74c3c',
        })
      }
    }

    // PROCESS RUNNING GAME
    if (t.eventType === 'RUNNER_ADVANCE') {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
      const rTxn = t as RunnerAdvanceTransaction

      // Filter strictly to running game mechanisms
      if (['PICKOFF', 'PO', 'SB', 'STOLEN_BASE', 'PB'].includes(rTxn.advanceReason || '')) {
        const endBase = rTxn.endBase === 'H' ? 'Home' : rTxn.endBase + 'B'
        const isOutStr = rTxn.isOut ? 'Out' : 'Safe'

        // Find the runner's uniform number
        const rEntry = allGamePlayers.find(p => p.player.id === rTxn.runnerId)
        const runnerName = rEntry ? `Runner #${rEntry.player.uniformNumber}` : 'Runner'

        // Map Fielding Mechanics
        let mechStr = ''
        if (rTxn.playSequence && rTxn.playSequence.length > 0) {
          mechStr = rTxn.playSequence.map((s: SequenceItem) => {
            let pStr = String(s.position)
            if (s.isError) pStr += 'E'

            const details = []
            if (s.approach && s.approach !== 'routine') details.push(s.approach)
            if (s.execution && s.execution !== 'clean') details.push(s.execution)
            if (s.throw && s.throw !== 'not_required') details.push(s.throw)

            if (details.length > 0) pStr += ` (${details.join(', ')})`
            return pStr
          }).join(' - ')
        }

        const detailsStr = mechStr
          ? `${runnerName} to ${endBase}: [${mechStr}] ➔ ${isOutStr}`
          : `${runnerName} to ${endBase} ➔ ${isOutStr}`

        if (['PICKOFF', 'PO'].includes(rTxn.advanceReason || '')) {
          stat.pickoffs.push(detailsStr)
        } else if (['SB', 'STOLEN_BASE'].includes(rTxn.advanceReason || '')) {
          stat.stolenBases.push(detailsStr)
        } else if (rTxn.advanceReason === 'PB') {
          stat.passedBalls.push(detailsStr)
        }
      }
    }
  })

  // Post-Process: Attach Catchers to Pitchers
  statsMap.forEach((stat, pId) => {
    // Find all PAs where this pitcher pitched
    const pitcherPAs = gameStore.plateAppearances.filter(pa => pa.pitcherId === pId)
    // Map position 2 (Catcher) from defense snapshots
    const catcherIds = new Set(pitcherPAs.map(pa => pa.defenseSnapshot?.[2]?.playerId).filter(Boolean))

    stat.catchers = Array.from(catcherIds).map(cId => {
      const c = allPlayers.find(p => p.player.id === cId)
      return c ? `#${c.player.uniformNumber} ${c.player.name}` : `Unknown`
    })
  })

  return Array.from(statsMap.values())
})

// --- PRINTER FRIENDLY PITCHING REPORT ---
const printPitchingSummary = () => {
  const printWindow = window.open('', '_blank')
  if (!printWindow) {
    console.error('Pop-up blocked. Could not open print window.')
    return
  }
  const teamName =
    props.teamLineup  === 'all'
      ? 'All Pitchers'
      : props.teamLineup === 'home'
        ? gameStore.homeTeamName
        : gameStore.visitingTeamName

  let html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Pitching Report - ${teamName}</title>
      <style>
        body { font-family: 'Helvetica Neue', Arial, sans-serif; line-height: 1.4; color: #000; padding: 20px; font-size: 12px; }
        h1 { margin-top: 0; padding-bottom: 10px; border-bottom: 2px solid #000; }
        .pitcher-card {
          page-break-inside: avoid;
          margin-bottom: 25px;
          border: 1px solid #ccc;
          padding: 15px;
          border-radius: 8px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .info-section { flex: 1; padding-right: 15px; }
        .header { font-size: 18px; font-weight: bold; margin-bottom: 8px; }
        .stat-line { font-size: 14px; margin-bottom: 4px; }
        .running-game { font-size: 12px; margin-top: 10px; border-top: 1px dashed #ccc; padding-top: 8px; }
        .running-game ul { padding-left: 20px; margin: 4px 0 10px 0; color: #444; }
        .charts-section { display: flex; gap: 15px; }
        .chart-container {
          text-align: center;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
          padding: 8px;
          background: #fff;
        }
        .chart-title { font-size: 9px; font-weight: bold; color: #1976D2; margin-bottom: 5px; letter-spacing: 0.5px; }
      </style>
    </head>
    <body>
      <h1>Pitching Report: ${teamName}</h1>
  `
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  activePitchingStats.value.forEach((pitcher:any) => {
    const strikePct = pitcher.total > 0 ? Math.round((pitcher.strikes / pitcher.total) * 100) : 0

    let pitchGridCircles = ''
    pitcher.pitches.forEach((p: { x: number; y: number; color: string }) => {
      pitchGridCircles += `<circle cx="${20 + p.x * 60}" cy="${25 + p.y * 50}" r="3.5" fill="${p.color}" stroke="#2c3e50" stroke-width="0.5" opacity="0.85" />`
    })

    const pitchGridSvg = `
      <div class="chart-container">
        <div class="chart-title">PITCHER POV</div>
        <svg viewBox="0 0 100 100" width="100" height="100" style="background-color: #f5f5f5; border-radius: 4px;">
          <rect x="20" y="25" width="60" height="50" fill="white" stroke="#333" stroke-width="1.5" />
          <line x1="20" y1="41.6" x2="80" y2="41.6" stroke="#999" stroke-width="0.5" />
          <line x1="20" y1="58.3" x2="80" y2="58.3" stroke="#999" stroke-width="0.5" />
          <line x1="40" y1="25" x2="40" y2="75" stroke="#999" stroke-width="0.5" />
          <line x1="60" y1="25" x2="60" y2="75" stroke="#999" stroke-width="0.5" />
          ${pitchGridCircles}
        </svg>
      </div>
    `

    let battedBallCircles = ''
    pitcher.battedBalls.forEach((hit: { x: number; y: number; color: string }) => {
      battedBallCircles += `<circle cx="${50 + hit.x * 75}" cy="${85 + hit.y * 75}" r="3.5" fill="${hit.color}" stroke="#2c3e50" stroke-width="0.5" opacity="0.85" />`
    })

    const battedBallsSvg = `
      <div class="chart-container">
        <div class="chart-title">BATTED BALLS</div>
        <svg viewBox="0 0 100 100" width="100" height="100" style="background-color: #f5f5f5; border-radius: 4px;">
          <path d="M 7.6 42.6 Q 50 15 92.4 42.6 L 50 85 Z" fill="white" stroke="#ccc" stroke-width="1.5" />
          <path d="M 30 65 Q 50 50 70 65 L 50 85 Z" fill="#eee" stroke="#ccc" stroke-width="1" />
          <path d="M 50 82 L 40 72 L 50 62 L 60 72 Z" fill="none" stroke="#ccc" stroke-width="1" />
          ${battedBallCircles}
        </svg>
      </div>
    `

    // Build the dynamic string for running game data
    let runningGameHtml = `<div class="running-game">`
    runningGameHtml += `<div><strong>Catcher(s):</strong> ${pitcher.catchers.join(', ') || 'Unknown'}</div>`

    if (pitcher.pickoffs.length > 0) {
      runningGameHtml += `<div style="margin-top: 6px;"><strong>Pickoffs:</strong><ul>`
      pitcher.pickoffs.forEach((po:string) => runningGameHtml += `<li>${po}</li>`)
      runningGameHtml += `</ul></div>`
    }
    if (pitcher.stolenBases.length > 0) {
      runningGameHtml += `<div style="margin-top: 6px;"><strong>Stolen Bases:</strong><ul>`
      pitcher.stolenBases.forEach((sb:string) => runningGameHtml += `<li>${sb}</li>`)
      runningGameHtml += `</ul></div>`
    }
    if (pitcher.passedBalls.length > 0) {
      runningGameHtml += `<div style="margin-top: 6px;"><strong>Passed Balls:</strong><ul>`
      pitcher.passedBalls.forEach((pb:string) => runningGameHtml += `<li>${pb}</li>`)
      runningGameHtml += `</ul></div>`
    }
    runningGameHtml += `</div>`

    html += `
      <div class="pitcher-card">
        <div class="info-section">
          <div class="header">#${pitcher.uniformNumber} - ${pitcher.name}</div>
          <div class="stat-line">Total Pitches: <strong>${pitcher.total}</strong></div>
          <div class="stat-line">
            <span style="color: #2e7d32; font-weight: bold;">Balls: ${pitcher.balls}</span> |
            <span style="color: #c62828; font-weight: bold;">Strikes: ${pitcher.strikes}</span>
          </div>
          <div class="stat-line" style="color: #666; font-style: italic;">(${strikePct}% Strikes)</div>
          ${runningGameHtml}
        </div>

        <div class="charts-section">
          ${pitchGridSvg}
          ${battedBallsSvg}
        </div>
      </div>
    `
  })

  html +=
    ' <script> ' +
    ' window.onload = () => { ' +
    ' window.print() } ' +
    ' <\u002Fscript><\u002Fbody><\u002Fhtml>'

  printWindow.document.open()
  printWindow.document.write(html)
  printWindow.document.close()
}
</script>
<style scoped>
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.sticky-top {
  position: sticky;
  top: 0;
  z-index: 10;
}
.top-z {
  z-index: 10;
}
</style>
